import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { PageHeader, Section } from "@/components/Primitives";
import { ALL_PAGES } from "@/data/config";
import { ArrowUpRight } from "lucide-react";

const GROUP_META = {
  Main: { pill: "Main", id: "sitemap-main" },
  Services: { pill: "Services", id: "sitemap-services" },
  "Service Areas": { pill: "Areas", id: "sitemap-areas" },
  "Service by City": { pill: "Combinations", id: "sitemap-combinations" },
};

export default function Sitemap() {
  const pages = ALL_PAGES();
  const groups = pages.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  const jumpTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    if (typeof window !== "undefined" && window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -96 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEO
        title="HTML Sitemap | All Pages"
        description="Every page on Spokane Gutter Co. — services, service areas, service and city combinations, about, contact."
        path="/sitemap"
      />
      <PageHeader
        overline="Full site index"
        title={`Every page on the site. ${pages.length} in total.`}
        lede="Human readable index so you can jump straight to a service, a city, or a service and city combination."
      />
      <div
        data-testid="sitemap-pill-nav"
        className="sticky top-[72px] md:top-[88px] z-30 bg-ink-950/95 backdrop-blur-md border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-4 flex items-center gap-2 overflow-x-auto">
          {Object.entries(groups).map(([g], i) => {
            const meta = GROUP_META[g] || { pill: g, id: `sitemap-group-${i}` };
            return (
              <a
                key={g}
                href={`#${meta.id}`}
                onClick={jumpTo(meta.id)}
                data-testid={`sitemap-pill-${meta.pill.toLowerCase()}`}
                className="shrink-0 font-heading text-xs uppercase tracking-[0.18em] text-bone-300 hover:text-ink-950 hover:bg-copper-500 border border-white/15 hover:border-copper-500 rounded-full px-4 py-2 transition-colors duration-200"
              >
                {meta.pill}
              </a>
            );
          })}
        </div>
      </div>
      <Section testid="sitemap-body">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {Object.entries(groups).map(([g, list]) => {
            const meta = GROUP_META[g] || { pill: g, id: `sitemap-group-${g.toLowerCase().replace(/\s+/g, "-")}` };
            return (
              <div
                key={g}
                id={meta.id}
                className={
                  g === "Service by City"
                    ? "md:col-span-12 border-t border-white/10 pt-10 scroll-mt-32"
                    : "md:col-span-4 border-t border-white/10 pt-10 scroll-mt-32"
                }
                data-testid={`sitemap-group-${g.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="overline mb-6">{g}</p>
                <ul
                  className={
                    g === "Service by City"
                      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
                      : "space-y-3"
                  }
                >
                  {list.map((p) => (
                    <li key={p.path}>
                      <Link
                        to={p.path}
                        className="group inline-flex items-center gap-2 text-bone-100 hover:text-copper-500 text-base"
                      >
                        <ArrowUpRight className="w-3.5 h-3.5 text-bone-400 group-hover:text-copper-500 group-hover:rotate-45 transition-transform" />
                        {p.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}

